import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/shared/components/ui';
import { ErrorState } from '@/shared/components/ui/ErrorState';
import { commonMsgs } from '@/shared/i18n/common';
import { translate, type MessageDef } from '@/shared/i18n/define';
import { store } from '@/shared/store';

interface Props {
  children: ReactNode;
  /** Pantalla completa (raíz) o solo el recuadro de contenido. */
  fullPage?: boolean;
  /** Cambia esta clave (p. ej. la ruta) para salir del estado de error al navegar. */
  resetKey?: string;
}

interface State {
  error: Error | null;
  resetKey?: string;
}

/**
 * Última red de seguridad: si una pantalla lanza una excepción al pintar, se muestra una pantalla amable con
 * reintento y recarga en vez de dejar la app en blanco. Los textos salen del store sin hooks (la clase
 * no puede usarlos y el árbol de React ya falló).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const lang = store.getState().lang.current;
    const t = (key: string) => translate(commonMsgs as unknown as MessageDef, lang, key);
    const view = (
      <ErrorState
        title={t('states.crashTitle')}
        description={t('states.crashText')}
        detail={this.state.error.name}
        onRetry={() => this.setState({ error: null })}
        retryLabel={t('actions.retry')}
      />
    );
    if (!this.props.fullPage) return view;
    return (
      <div className="grid min-h-dvh place-items-center bg-bg p-4">
        <div className="surface-raised w-full max-w-md rounded-lg">
          {view}
          <div className="flex justify-center pb-6">
            <Button variant="ghost" onClick={() => window.location.reload()}>
              {t('actions.reload')}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
